import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated, selectCurrentUser } from '../../features/auth/authSlice';
import { useGetWorkspaceDashboardQuery } from '../../features/api/apiSlice';
import { isProfessionalRole } from '../../lib/authPortals';
import WorkspaceShell from './WorkspaceShell';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const cards = [
  {
    to: '/workspace/co-broker',
    icon: 'Users',
    title: 'Co-Brokering Network',
    desc: 'Split commissions, invite a partner agent, and sign in-app co-broker agreements.',
    key: 'co_broker',
  },
  {
    to: '/workspace/crm',
    icon: 'Contact',
    title: 'Built-in CRM',
    desc: 'Track leads, property interest, and follow-up reminders in one place.',
    key: 'crm',
  },
  {
    to: '/workspace/bookings',
    icon: 'Calendar',
    title: 'Advisory Booking',
    desc: 'Publish availability and let clients or agents book consultations.',
    key: 'bookings',
  },
  {
    to: '/workspace/documents',
    icon: 'FileText',
    title: 'Digital Document Signing',
    desc: 'Create viewing forms, intent letters, and collect typed e-signatures.',
    key: 'documents',
  },
];

const WorkspaceHome = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectCurrentUser);
  const navigate = useNavigate();
  const isAgent = isProfessionalRole(user?.account_role);
  const { data, isLoading, error } = useGetWorkspaceDashboardQuery(undefined, {
    skip: !isAuthenticated || !isAgent,
  });

  if (!isAuthenticated) {
    return (
      <WorkspaceShell title="Agent Workspace" subtitle="Sign in as a Real Estate Agent to collaborate and close deals.">
        <div className="rounded-xl border border-border bg-card p-8 text-center">
          <p className="mb-4 text-muted-foreground">This workspace is for agents, brokers, and advisors.</p>
          <Button onClick={() => navigate('/pro/login')}>Real Estate Agent Login</Button>
        </div>
      </WorkspaceShell>
    );
  }

  if (!isAgent) {
    return (
      <WorkspaceShell title="Agent Workspace" subtitle="Upgrade to a Real Estate Agent account to use collaboration tools.">
        <div className="rounded-xl border border-border bg-card p-8 text-center space-y-3">
          <p className="text-muted-foreground">
            Your account is registered as a user. Register or verify as an agent, broker, or advisor to open the workspace.
          </p>
          <div className="flex justify-center gap-3">
            <Button onClick={() => navigate('/pro/register')}>Become a Real Estate Agent</Button>
            <Button variant="outline" onClick={() => navigate('/agent-dashboard/verification')}>Get Verified</Button>
          </div>
        </div>
      </WorkspaceShell>
    );
  }

  const stats = data || {};

  return (
    <WorkspaceShell
      title="How you work together"
      subtitle="Close deals with co-brokering, CRM, advisory bookings, and digital signing — not just listings."
    >
      {isLoading && <p className="text-muted-foreground">Loading workspace…</p>}
      {error && (
        <p className="text-error text-sm mb-4">
          Could not load workspace stats. You can still open the tools below.
        </p>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <Stat label="Co-broker pending" value={stats.co_broker?.pending ?? '—'} />
        <Stat label="Open CRM leads" value={stats.crm?.leads_open ?? '—'} />
        <Stat label="Follow-ups due" value={stats.crm?.follow_ups_due ?? '—'} />
        <Stat label="Docs awaiting signature" value={stats.documents?.awaiting_my_signature ?? '—'} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {cards.map((c) => (
          <Link
            key={c.to}
            to={c.to}
            className="block rounded-xl border border-border bg-card p-5 hover:border-primary/40 hover:shadow-sm transition-all"
          >
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-lg bg-secondary/15 flex items-center justify-center">
                <Icon name={c.icon} size={22} className="text-secondary" />
              </div>
              <div>
                <h2 className="font-semibold text-lg text-foreground">{c.title}</h2>
                <p className="text-sm text-muted-foreground mt-1">{c.desc}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </WorkspaceShell>
  );
};

const Stat = ({ label, value }) => (
  <div className="rounded-xl border border-border bg-card p-4">
    <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
    <p className="text-2xl font-bold mt-1">{value}</p>
  </div>
);

export default WorkspaceHome;
